import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../services/notifications';

// NotificationMediumID.APP = 3 (backend enum) — the in-app feed medium.
const APP_MEDIUM_ID = 3;

// Relative "time ago" for the notification list.
const timeAgo = (iso) => {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Resolve the in-app route for a notification from its payload + the caller's
// persona. The interior-designer payload carries { idLeadId, ... } and the
// marketplace (architect/freelancer) payload carries { archProjectId }. Each
// persona deep-links to its own detail surface; buyers are served on mobile, so
// they get no navigation.
const routeForNotification = (notification, persona) => {
  const payload = notification.payload || {};
  const idLeadId = payload.idLeadId ?? payload.id_lead_id;
  const archProjectId = payload.archProjectId ?? payload.arch_project_id;
  const buyerUserId = payload.buyerUserId ?? payload.buyer_user_id;
  const interiorDesignerId = payload.interiorDesignerId ?? payload.interior_designer_id;
  const referralId = payload.referralId ?? payload.referral_id;
  if (persona === 'INTERIOR_DESIGNER' && idLeadId) {
    return `/interior-designer/leads/${idLeadId}`;
  }
  if (persona === 'ARCHITECT' && archProjectId) {
    return `/architect/projects/${archProjectId}`;
  }
  if (persona === 'FREELANCER' && archProjectId) {
    return `/freelancer/projects/${archProjectId}`;
  }
  // Developer-side personas had no cases at all, so every notification they clicked did nothing.
  if (persona === 'ZUMALE_USER') {
    if (idLeadId) return `/zumale-admin/interior-executions/${idLeadId}`;
    if (referralId) return '/zumale-admin/referrals';
  }
  if (['BV_USER', 'DEVELOPER', 'ADMIN'].includes(persona)) {
    // The BV request screen is addressed by buyer AND designer — `idLeadId` alone cannot build it,
    // which is why the payload now carries both. Falling back to the buyers list keeps the click
    // meaningful for anything queued before that change.
    if (buyerUserId && interiorDesignerId) {
      return `/bv-user/buyer/${buyerUserId}/requests/${interiorDesignerId}`;
    }
    if (buyerUserId) return `/bv-user/buyer/${buyerUserId}/details`;
    if (idLeadId) return '/bv-user/buyers';
  }
  return null;
};

const NotificationBell = ({ pollIntervalMs = 60000 }) => {
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const persona = userInfo?.persona;

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const bellRef = useRef(null);

  const unreadCount = notifications.filter((n) => !(n.readAt ?? n.read_at)).length;

  const loadNotifications = useCallback(async () => {
    if (!persona) return;
    setLoading(true);
    try {
      const res = await fetchNotifications({ notificationMediumId: APP_MEDIUM_ID, page_size: 20 });
      const body = res?.data ?? res;
      const items = Array.isArray(body) ? body : body?.items ?? body?.results ?? [];
      setNotifications(items);
    } catch (e) {
      // Non-fatal — leave the list as-is.
    } finally {
      setLoading(false);
    }
  }, [persona]);

  // Initial load + polling.
  useEffect(() => {
    if (!persona) return undefined;
    loadNotifications();
    const id = setInterval(loadNotifications, pollIntervalMs);
    return () => clearInterval(id);
  }, [persona, pollIntervalMs, loadNotifications]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) loadNotifications();
  };

  const handleItemClick = async (notification) => {
    const id = notification.notificationId ?? notification.id;
    const isRead = Boolean(notification.readAt ?? notification.read_at);
    if (!isRead && id != null) {
      // Optimistic — flip locally, then persist (no-throw).
      setNotifications((prev) =>
        prev.map((n) =>
          (n.notificationId ?? n.id) === id ? { ...n, readAt: new Date().toISOString() } : n
        )
      );
      markNotificationRead(id);
    }
    const route = routeForNotification(notification, persona);
    setIsOpen(false);
    if (route) navigate(route);
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, readAt: n.readAt ?? n.read_at ?? new Date().toISOString() })));
    await markAllNotificationsRead();
  };

  if (!persona) return null;

  return (
    <div className="relative flex-shrink-0" ref={bellRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="relative flex items-center text-[#1d293b] hover:text-stats-orange p-1 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={18} className="animate-spin text-slate-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet</div>
            ) : (
              notifications.map((n) => {
                const id = n.notificationId ?? n.id;
                const isRead = Boolean(n.readAt ?? n.read_at);
                // Backend-presigned URL; attachmentUrl itself is a raw S3 key (used
                // only to sniff the file type since the key's extension is stable).
                const downloadUrl = n.attachmentDownloadUrl ?? n.attachment_download_url;
                const keyExt = (n.attachmentUrl ?? n.attachment_url ?? downloadUrl ?? '')
                  .split('?')[0]
                  .split('.')
                  .pop()
                  ?.toLowerCase();
                const isImage = Boolean(downloadUrl) && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(keyExt);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors hover:bg-slate-50 ${
                      isRead ? '' : 'bg-flamingo-50/40'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!isRead && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-flamingo-500" />}
                      <div className={`min-w-0 flex-1 ${isRead ? 'pl-4' : ''}`}>
                        <p className="text-sm text-slate-700 line-clamp-3">{n.text}</p>
                        {isImage && (
                          <img
                            src={downloadUrl}
                            alt="Notification attachment"
                            className="mt-2 h-24 w-full rounded-lg object-cover"
                            loading="lazy"
                          />
                        )}
                        {downloadUrl && !isImage && (
                          <span
                            role="link"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
                          >
                            View attachment
                          </span>
                        )}
                        <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.sentDate ?? n.sent_date)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
